/**
* Shared IndexedDB helper functions
* 
* Provides common utilities for IndexedDB operations across the codebase.
*/

export function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function promisifyVoidRequest(request: IDBRequest): Promise<void> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export function clearStore(store: IDBObjectStore): Promise<void> {
  return promisifyVoidRequest(store.clear());
}

export function putItem<T>(store: IDBObjectStore, item: T): Promise<void> {
  return promisifyVoidRequest(store.put(item));
}

export function getAllItems<T>(store: IDBObjectStore): Promise<T[]> {
  return promisifyRequest<T[]>(store.getAll());
}

export function getItem<T>(store: IDBObjectStore, key: IDBValidKey): Promise<T | undefined> {
  return promisifyRequest<T | undefined>(store.get(key));
}

export function countItems(store: IDBObjectStore): Promise<number> {
  return promisifyRequest<number>(store.count());
}

export function deleteDb(dbName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(dbName);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}

export function isIndexedDBAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

